import { useToast } from "@/components/ui/use-toast";
import {
  ClusterHardwareInfo,
  ClusterType,
  DynamicSupplyHardware,
  DynamicSupplyHardwareFiltersResponse,
  SimpleClusterForm
} from "@/types";
import { getMaxHardware, getMinHardware } from "@/utils/cluster";
import { useFormikContext } from "formik";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { OptionType } from "@/components/forms/inputs/SelectInput";
import {
  isHardwareInventoryUnavailableMessage,
  isHardwareNotKnownMessage,
  toFriendlyPublicError
} from "@/utils/api";
import { getDurationTypeLabel } from "@/utils/mapping";
import ENV_VARIABLES from "@/env";
import localCookies from "@/utils/cookie";
import { DEEP_LINK_GPU_COOKIE_KEY } from "@/constants/cluster";

type UseSummaryPanelLogicProps = {
  totalCost: string | number;
  fetchClusterHardwareInfo: (
    values: Partial<SimpleClusterForm>
  ) => Promise<ClusterHardwareInfo | null>;
  fetchHardwareWithFilters: (options?: {
    abortController?: AbortController;
    search?: string;
    filters?: Partial<DynamicSupplyHardwareFiltersResponse>;
  }) => Promise<{
    hardware: DynamicSupplyHardware[];
    filters: DynamicSupplyHardwareFiltersResponse;
  }>;
};

const MAX_HARDWARE_REFETCH_ATTEMPTS = 3;
const DURATION_QUANTITY_DEBOUNCE_MS = 300;

export const useSummaryPanelLogic = ({
  totalCost,
  fetchHardwareWithFilters,
  fetchClusterHardwareInfo,
  replicaCountFieldName = "replicaCount"
}: UseSummaryPanelLogicProps & { replicaCountFieldName?: string }) => {
  const { values, setFieldValue } = useFormikContext<SimpleClusterForm>();
  const [isPriceLoading, setIsPriceLoading] = useState(false);
  const [debouncedDurationQuantity, setDebouncedDurationQuantity] = useState(
    values.durationQuantity
  );
  const { toast } = useToast();
  const hardwareRefetchAttemptsRef = useRef(0);
  const isHardwareRefetchingRef = useRef(false);
  const priceRequestIdRef = useRef(0);

  const silentlyRefetchHardwareList = useCallback(async () => {
    if (isHardwareRefetchingRef.current) return;
    if (hardwareRefetchAttemptsRef.current >= MAX_HARDWARE_REFETCH_ATTEMPTS) return;
    isHardwareRefetchingRef.current = true;
    hardwareRefetchAttemptsRef.current += 1;
    try {
      const { hardware } = await fetchHardwareWithFilters();
      if (!hardware || hardware.length === 0) return;
      setFieldValue(
        "createClusterData",
        {
          ...(values.createClusterData || {}),
          hardwareList: hardware
        },
        false
      );
    } catch {
      // silent refetch failure
    } finally {
      isHardwareRefetchingRef.current = false;
    }
  }, [fetchHardwareWithFilters, setFieldValue, values.createClusterData]);

  useEffect(() => {
    hardwareRefetchAttemptsRef.current = 0;
  }, [values.hardware, values.deploymentId]);

  const durationOptions = useMemo(() => {
    const baseOptions = values?.createClusterData?.durations?.map(({ duration }) => ({
      value: duration,
      label: getDurationTypeLabel(duration)
    })) || [
      { value: "hourly", label: "Hours" },
      { value: "daily", label: "Days" },
      { value: "weekly", label: "Weeks" },
      { value: "monthly", label: "Months" }
    ];

    if (
      values.clusterType === ClusterType.KUBERNETES &&
      ENV_VARIABLES.ENVIRONMENT === "production"
    ) {
      return baseOptions.filter((option) => option.value !== "hourly");
    }

    return baseOptions;
  }, [values?.createClusterData?.durations, values.clusterType]);

  const replicaCountValue = values[replicaCountFieldName as keyof SimpleClusterForm];
  const [debouncedReplicaCountValue, setDebouncedReplicaCountValue] = useState(replicaCountValue);

  const availableQuantity = useMemo(() => {
    if (!values.hardwareInfo?.availableQuantity) {
      return [];
    }
    const options = values.hardwareInfo.availableQuantity as OptionType[];

    // Replica counts for container/k8s flows are not GPU hardware quantity limits.
    if (
      values.clusterType === ClusterType.CONTAINER ||
      values.clusterType === ClusterType.KUBERNETES
    ) {
      return options;
    }

    return (
      options.filter(({ value }) => {
        const numericValue = parseInt(value, 10);
        return (
          numericValue >= getMinHardware(values.clusterType as ClusterType) &&
          numericValue <= getMaxHardware(values.clusterType as ClusterType)
        );
      }) || []
    );
  }, [values.hardwareInfo, values.clusterType]);

  useEffect(() => {
    // Seed only when the field has never been set — not when the user clears it mid-edit.
    if (replicaCountValue !== undefined && replicaCountValue !== null) return;
    const nums = availableQuantity
      .map(({ value }) => parseInt(value, 10))
      .filter((n) => !Number.isNaN(n));
    if (nums.length === 0) return;
    setFieldValue(replicaCountFieldName, String(Math.min(...nums)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableQuantity, setFieldValue, replicaCountFieldName]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedDurationQuantity(values.durationQuantity);
    }, DURATION_QUANTITY_DEBOUNCE_MS);

    return () => clearTimeout(timeoutId);
  }, [values.durationQuantity]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedReplicaCountValue(replicaCountValue);
    }, DURATION_QUANTITY_DEBOUNCE_MS);

    return () => clearTimeout(timeoutId);
  }, [replicaCountValue]);

  useEffect(() => {
    const requestId = ++priceRequestIdRef.current;
    const load = async () => {
      try {
        if (!debouncedDurationQuantity || !values.hardware || !values.deploymentId) return;

        const parsedReplicaCount = parseInt(String(debouncedReplicaCountValue ?? ""), 10);
        // Skip price calls while replica count is empty / invalid (e.g. user cleared the field).
        if (!Number.isFinite(parsedReplicaCount) || parsedReplicaCount < 1) return;

        setIsPriceLoading(true);
        const normalizedReplicaCount = String(parsedReplicaCount);
        const clusterHardwarePricing = await fetchClusterHardwareInfo({
          ...values,
          [replicaCountFieldName]: normalizedReplicaCount,
          // Keep Formik field + API param in sync even when using a custom field name.
          replicaCount: normalizedReplicaCount,
          locations: values.locations?.map((location) => `"${location}"`),
          currency: values.currency,
          hardware: values.deploymentId
        });
        if (requestId !== priceRequestIdRef.current) return;
        if (clusterHardwarePricing) {
          setFieldValue("hardwareInfo", clusterHardwarePricing);
        }
      } catch (error) {
        if (requestId !== priceRequestIdRef.current) return;
        const errorMessage = toFriendlyPublicError(error);
        if (errorMessage instanceof Error) {
          // Inventory changed mid-flow (sold out / deploy_id invalid): refresh list
          // so cards reflect sold-out state instead of leaving a stale selection.
          if (isHardwareInventoryUnavailableMessage(errorMessage.message)) {
            await silentlyRefetchHardwareList();
            // "No hardware found" is recovered silently (COMP-912); "not known" is the
            // out-of-stock race and should explain why price/deploy cannot continue.
            if (isHardwareNotKnownMessage(errorMessage.message)) {
              toast({ title: errorMessage.message, variant: "error" });
            }
          } else if (
            errorMessage.message === "Minimal amount of GPUs for this hardware type is 8."
          ) {
            localCookies.setItem(DEEP_LINK_GPU_COOKIE_KEY, "");
          } else {
            toast({ title: "Failed to fetch price please try again", variant: "error" });
          }
        } else {
          toast({ title: "Failed to fetch price please try again", variant: "error" });
        }
      } finally {
        if (requestId === priceRequestIdRef.current) {
          setIsPriceLoading(false);
        }
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    debouncedDurationQuantity,
    values.durationType,
    values.deploymentId,
    values.locations,
    values.currency,
    values.hardware,
    debouncedReplicaCountValue,
    values.gpusPerNode,
    values.gpusPerContainer
  ]);

  const fetchedTotalCost = Number(values.hardwareInfo?.totalCost || 0);
  const fallbackTotalCost =
    typeof totalCost === "string"
      ? parseFloat(totalCost.replace(/[^0-9.]/g, ""))
      : Number(totalCost || 0);
  const totalCostNumber =
    isNaN(fetchedTotalCost) || fetchedTotalCost <= 0 ? fallbackTotalCost : fetchedTotalCost;

  return {
    values,
    setFieldValue,
    durationOptions,
    isPriceLoading,
    totalCostNumber,
    availableQuantity
  };
};
